/**
 * Cross-compatible feature flag key registry.
 *
 * Separate from `./featureFlags.ts` because that file is `"use client"` (it
 * exports React hooks). Per CLAUDE.md, server modules MUST NOT import from
 * `"use client"` files — when they do, the imported values become client-
 * reference proxies and resolve to `undefined` at runtime in production.
 * Server code imports the keys from here; client code re-exports them via
 * `./featureFlags.ts` alongside the hooks.
 */
export const FEATURE_FLAGS = {
  // The whole Lev mobile surface, gating reach and discovery with one switch.
  // Server-side (`isMobileAppEnabled`) it is the ACCESS gate: OFF and
  // `/{projectId}/m/**` and `/m` 404. Client-side
  // (`useFeatureFlag`) it also decides whether a phone opening a project is
  // redirected to the mobile app instead of the canvas.
  // Fail-closed in both: OFF, absent, or PostHog unreachable all read as off,
  // which is today's behavior. One flag rather than two because "can I reach
  // Lev mobile" and "does my phone go there by default" are the same question
  // for a founder, and a second flag would only add a state where the redirect
  // points somewhere they cannot open.
  MOBILE_APP: "mobile-app",
  WEEKLY_REPORT_EMAILS: "weekly-report-emails",
  // Gates the Sunday Competitors market-update crawl end-to-end: cron fan-out
  // AND per-project scan (CSV append + WeeklyReport.marketUpdate). Only owners
  // with the flag ON are crawled. OFF → no dispatch, no scan, no CSV write.
  // Monday renders any already-staged marketUpdate regardless of this flag
  // (payload is the include flag). Fail-closed. Per-owner.
  // Staging / production: flag not created yet — fail-closed until enabled
  // (allowlist first). Do not claim a rollout percentage here.
  WEEKLY_REPORT_MARKET_UPDATE: "weekly-report-market-update",
  APPLICANTS_ENABLED: "applicants-enabled",
  AGENT_SANDBOX: "agent-sandbox",
  INVESTOR_UPDATES: "feature-investor-updates",
  // LEV-1010: gate the Orbit "Scan" bulk-action (Universe cache enrichment).
  // Default OFF in production — toggled on after staging QA.
  UNIVERSE_SCAN: "universe-scan",
  // LEV-1360: gate server-authoritative block auto-generation cascade. Skipped on
  // proto projects (Project.protoBlocksEnabled) whatever this flag says — see
  // PROTO_BLOCKS; both flags may be ON together.
  BLOCK_AUTO_GEN: "block-auto-gen",
  // proto-blocks (ADR 056): fact extraction, PROTO blocks at creation, "Improve
  // this"/"Improve all", onboarding steering. Evaluated SERVER-SIDE once per
  // onboarding session and stamped (OnboardingSession → Project.protoBlocksEnabled);
  // later reads use the stamp, never a fresh evaluation. OFF = staging behavior.
  //
  // Exclusive with BLOCK_AUTO_GEN PER PROJECT, not per flag: both flags may be ON
  // at once. The cascade runs only when block-auto-gen is ON AND the project's
  // protoBlocksEnabled stamp is false (AutoCascadeAuthorizationService), so proto
  // projects are built via "Improve all" / the ghost card while non-proto
  // projects keep auto-generating. Never turn block-auto-gen off for proto-blocks.
  //
  // INVARIANT: turning the flag OFF cannot expose proto text as real content.
  // PROTO rows persist, and every approve/regenerate guard keys on ROW STATUS
  // (isProtoBlock), not on the flag — a PROTO row stays enhance-only regardless.
  PROTO_BLOCKS: "proto-blocks",
  // proto-blocks module 7 (onboarding-steering): the flag PAYLOAD (not the
  // boolean value) carries the SteeringConfig — per-stage fact weights, the
  // up/down scalars, the gap-answer turn budget and the [FACT_GAPS] cap — so
  // operators can retune onboarding steering per cohort with no deploy. Read
  // server-side via getFeatureFlagPayload; missing/malformed payloads fall back
  // to DEFAULT_STEERING_CONFIG. The steering BEHAVIOR itself is gated by
  // PROTO_BLOCKS, not by this flag — this flag only tunes it, so it only affects
  // proto-blocks sessions (where the block-auto-gen cascade is skipped per project).
  ONBOARDING_STEERING: "onboarding-steering",
  // LEV-1196: gate the agentic Apollo prospecting flow (prospecting.start +
  // the "Add from Apollo" entry points). Each run provisions a sandbox,
  // mints an OpenAI key, and spends Apollo credits, so it stays flag-gated.
  // User-scoped. Default OFF.
  PROSPECTING_AGENT: "prospecting-agent",
  // LEV-1231 / PR #406: gate the Team sidebar experience. ON → Users icon,
  // "Team" tooltip, TeamLightbox (Agents/Humans/Guests). OFF (production
  // default) → legacy Share2 icon, "Share project"/"Share settings", and the
  // restored ShareSettingsModal. User-scoped (client hook is the primary
  // gate).
  AGENT_TEAM: "agent-team",
  // LEV-1231 / PR #406: secondary gate to AGENT_TEAM (keep this flag's cohort
  // a subset of agent-team's). Gates agents in the comment @-mention
  // autocomplete, the "X is working…" shimmer, and — server-side, in
  // comment.create — agent reply routing + agent_mentioned analytics.
  // User-scoped. Default OFF.
  AGENT_TEAM_MENTIONS: "agent-team-mentions",
  // Gate the visual roadmap board. ON → adds an icon-only roadmap opener to the
  // task-list header that opens the roadmap board lightbox (+ its onboarding
  // overlay and real-project backing). OFF (production default) → no roadmap
  // icon. The sidebar task list, the open-task lightbox, and the eval page are
  // always the normal (non-roadmap) versions regardless of this flag. User-scoped
  // (client hook is the primary gate). Default OFF for external users.
  TASK_ROADMAP: "task-roadmap",
  // LEV-1394: gate the anonymous project creation flow. When ON, onboarding
  // completion creates a project without requiring sign-up and redirects
  // straight to the canvas. When OFF (production default), the existing
  // sign-up-first flow is byte-identical. User-scoped (evaluated with the
  // visitor's anonymous PostHog distinct id). Default OFF.
  ANONYMOUS_PROJECTS: "anonymous-projects",
  // LEV-1396 / LEV-1637: configurable per-cohort signup gate for anonymous
  // projects. The flag PAYLOAD (not the boolean value) carries a
  // SignupGateConfig object ({ gatedActions, enabled, gatedPrimaryCount }) so
  // different visitor cohorts can receive different gate strictness via PostHog
  // experiments. `gatedPrimaryCount` (positive integer, else no limit) caps how
  // many primary blocks an anonymous project may hold. An explicit
  // `gatedActions: []` opens only the four configurable keys (block.edit,
  // evaluation.run, project.share, project.settings); pipeline.access,
  // task.manage, and account are fixed anonymous boundaries the payload cannot
  // open. Missing or malformed arrays fall back to defaults (all five actions
  // gated, no primary cap). `enabled:false` likewise lifts only the configurable
  // keys plus the primary cap — the three fixed keys stay refused.
  // Evaluated with the visitor's anonymous PostHog distinct id.
  ANONYMOUS_PROJECT_GATE: "anonymous-project-gate",
  // Gates the "conscious note-taker" manual-completion flow. ON → clicking
  // "Mark Task Complete" on a human-only task opens a guided in-thread
  // conversation: Lev asks 1–3 targeted questions, evaluates the founder's
  // answer (one follow-up max), then proposes a canvas update to the task's
  // related block that the founder accepts (→ completes the task,
  // completedBy "user") or rejects (→ revision loop). The task shows "Need
  // your approval" for the whole flow. OFF (production default) → the legacy
  // free-text "What did you do?" notes textarea completes the task directly.
  // User-scoped (client hook gates the UI; the server mutations fail closed).
  // Default OFF in production.
  TASK_NOTES_V3: "task-notes-v3",
  // LEV-1659: system-level kill switch for the weekly SEC Form D ingest cron
  // (venture-graph/ingest-weekly). Read as `system`, like WEEKLY_REPORT_EMAILS.
  // OFF → the run writes a `skipped` VentureIngestRun row and exits before any
  // SEC fetch. Fail-closed when PostHog is unreachable so a flag outage cannot
  // start unbounded SEC traffic.
  VENTURE_GRAPH_INGEST: "venture-graph-ingest",
  // LEV-1891: system-level kill switch for founder-candidate filing in the
  // review queue after Investor Research generation. Independent of other
  // venture-graph flags. ON = when Investor Research generation finishes, firms
  // with no Venture Graph match are filed as `firmCandidate` cards (source
  // "founder") in the review queue, counted once per founder project. OFF = nothing
  // is filed and behavior is identical to before. Fail-closed.
  VENTURE_GRAPH_FOUNDER_CANDIDATES: "venture-graph-founder-candidates",
  // LEV-2442: system-level kill switch for the weekly program catalog rescan
  // cron (src/lib/inngest/programCatalogRescan.ts). Read as `system`, like
  // VENTURE_GRAPH_INGEST. ON = every Monday the job researches each catalog
  // program on its own website and opens or updates one draft GitHub PR; a
  // person still merges. OFF = the job exits before any model call, page fetch
  // or GitHub request. Fail-closed when PostHog is unreachable so an outage
  // cannot start paid web research. Independent of the Programs page flags.
  PROGRAM_CATALOG_RESCAN: "program-catalog-rescan",
  // Suggested-add lane (LEV-2282 spec): system-level kill switch for the weekly
  // venture-graph-suggestions job, its event dispatch, and `queue.suggested`.
  // ON = the job stamps safe open neighbor cards with a one-click Add, and the
  // review UI shows a "Suggested adds" section first. OFF = the job writes no
  // stamp (and returns `{ skipped: true }` unless
  // VENTURE_GRAPH_NEIGHBOR_CRAWL_WALK is on), the router returns an empty result
  // with no DB read, and the review UI is byte-identical to before this
  // feature existed. Independent of VENTURE_GRAPH_INGEST — this flag only
  // governs suggesting adds, not the weekly SEC ingest that feeds the graph.
  // A planned sibling `venture-graph-suggested-adds-activate` will later gate
  // auto-activation of a stamped card; this flag alone never auto-activates
  // anything. Fail-closed when PostHog is unreachable.
  VENTURE_GRAPH_SUGGESTED_ADDS: "venture-graph-suggested-adds",
  // ON: the venture-graph-suggestions job opens a neighbor card for each firm
  // family its co-investor crawl reaches, does not reject (qualifies or
  // borderline), and finds no open neighbor card for; the weekly ingest
  // requests that job. OFF: the job opens no card, and the weekly ingest's own
  // neighbor pass, which is the same either way, stays the only opener.
  // Independent of VENTURE_GRAPH_SUGGESTED_ADDS: this flag alone opens cards
  // and stamps none, and a card it opens is stamped no earlier than the next
  // run. Read as "system"; fail-closed (OFF) when PostHog is unreachable.
  VENTURE_GRAPH_NEIGHBOR_CRAWL_WALK: "venture-graph-neighbor-crawl-walk",
  // LEV-2094: ON: after a new anonymous or signup-first project's first
  // session goes idle, one digest per project posts to Slack (#prod-new-users
  // for both routes); and a founder who signed up 3 hours ago but owns and
  // shares no project gets one "no project yet" post there (LEV-2322).
  // OFF: no digest event is sent, no-project runs exit before their wait,
  // and waiting runs of both exit without posting. Read as `system`. Since
  // 2026-10-11 the PostHog 🌱/🎉 posts are off, so OFF leaves only the ✅
  // claimed-project post: most new founders go unannounced.
  FIRST_SIGHTING_DIGEST: "first-sighting-digest",
  // Moves all Lev-directed interaction out of the task lightbox composer and into
  // the LevAI side chat, scoped to the open task. ON → the lightbox composer is
  // collaborator/plain-comment only (Lev removed from the @-mention pool; a typed
  // "@Lev" is intercepted and redirected into the chat); questions, edit
  // requests, "do it", and the Task Notes v3 completion Q&A all happen in the
  // side chat, which auto-expands when Lev initiates. OFF (production default) →
  // today's behavior byte-for-byte: @Lev routing + completion Q&A live in the
  // lightbox composer. Lev's OUTCOMES (proposal drafts, approval requests,
  // completions) keep emitting to the shared DB-backed feed regardless of the
  // flag — only the CONVERSATION moves to the private per-user chat. The
  // "Approved" comment shortcut and teammate @-mention notifications are
  // unaffected. Client hook gates the composer/redirect UI; the server
  // `addComment` agent-conversation kickoff is gated to fail closed to today's
  // behavior. Default OFF in production. With task-progress-sync ON, the
  // composer still shows on open human tasks for a solo founder (as "Update Lev
  // with your progress"), overriding this flag's solo-hidden bar.
  TASK_LEV_TO_CHAT: "task-lev-to-chat",
  // LEV-1486: gates bulk select (checkbox on kanban cards) + the bulk action
  // bar (Delete, Move to) on the pipeline Kanban board. OFF (default) →
  // today's behavior byte-for-byte: no checkboxes, no bulk bar, single-entry
  // delete/move only. Client hook gates the checkbox/bar UI; the
  // `pipelines.bulkDeleteEntries`/`bulkMoveEntries` mutations are gated
  // server-authoritatively so the write path can't be reached with the flag
  // off even via direct tRPC calls. ON with `pipeline-tour` also adds the
  // Customers tour's "select" stop (kanban view only); OFF leaves the tour at
  // its seven stops.
  PIPELINE_BULK_ACTIONS: "pipeline-bulk-actions",
  // Progress updates on open human tasks. ON: the comment composer on an open
  // human task reads "Update Lev with your progress" and shows even for a solo
  // founder under task-lev-to-chat; each update from someone who can complete
  // the task is classified, and when it reports real work Lev drafts a canvas
  // proposal whose Accept applies the edit WITHOUT completing the task. The
  // optional completion-notes box is treated as the last update. OFF (default)
  // = today's behavior byte-for-byte: no classification, no progress drafts,
  // composer copy and visibility unchanged. Keyed on the PROJECT OWNER's
  // distinct id at every surface (tasks.getDetail, activity.addComment,
  // tasks.setStatus). Fail-closed (false) when PostHog is unavailable.
  TASK_PROGRESS_SYNC: "task-progress-sync",
  // LEV-1686: kill switch for LLM usage tracking. ON = LLM usage rows and
  // $ai_generation events are written. OFF = no tracking writes, model calls
  // unaffected. Evaluated server-side once per request boundary, never per
  // model call. Fail-closed.
  LLM_USAGE_TRACKING: "llm-usage-tracking",
  // Gates rule 2 of deriveSuggestedNextAction. ON → the next action is the
  // oldest unapproved draft ("Review X") ahead of any ranked build. OFF → drafts
  // never pre-empt; the next action follows the normal recommendation. Keyed
  // on the project owner. Server-only, fail-closed (OFF). Default OFF everywhere.
  FORCE_REVIEW_BLOCK_DRAFTS: "force-review-block-drafts",
  // LEV-1951 (ADR 057): kill switch. ON = force the standard deck parse
  // profile for proto-blocks sessions (turning the switch ON is what disables
  // fast); OFF, inactive, missing or unreadable = fast, the default. Evaluated
  // at the parse route with `anon:<tokenHash>` and carried in the event data.
  // Fail direction is fast: an unknown verdict must never slow a founder's
  // parse, so only an explicit PostHog `true` disables it.
  DECK_PARSE_FAST_DISABLED: "deck-parse-fast-disabled",
  // LEV-1792: archive and unarchive a project. ON, once every slice has
  // landed = the owner can archive; an archived project is frozen (no email,
  // no jobs, no writes, out of search) and the home page splits into Active /
  // Shared / Archived / Deleted sections. OFF = today's single flat grid with
  // no archive. Gates `projects.archive` only. The freeze itself (LEV-2013:
  // no writes, jobs, emails or canvas for an archived project) keys off
  // `Project.archivedAt` and never reads this flag, so OFF does not un-freeze
  // an already-archived project; `projects.unarchive` is therefore ungated so
  // recovery is always possible. Keyed on the acting user. Sibling of
  // PROJECT_HARD_DELETE — the two roll out independently and both may be ON.
  // Default OFF in production.
  // Kill-switch note: turning this OFF after projects were already archived
  // hides them from the home page (it falls back to the flat grid, which
  // never shows an archived row) until it's turned back ON — the server
  // `unarchive` mutation stays reachable the whole time, but the UI control to
  // call it disappears with the sectioned layout. Turn the flag back ON to
  // let founders see and unarchive those projects again.
  PROJECT_ARCHIVE: "project-archive",
  // LEV-1792: two-stage permanent deletion. ON = the delete dialog warns and
  // offers Archive instead, a deleted project sits in the Deleted section for
  // 30 days with Restore and a "Delete now" item, and the nightly purge job
  // destroys it after that. OFF = today's delete, which only hides the project
  // and never purges anything.
  // Split from PROJECT_ARCHIVE because this half destroys data: hold it at 0%
  // in production until the purge job has been watched in staging.
  // Gates `projects.deleteNow`, `projects.restore` and `projects.purgeNow`,
  // and is read per owner by the purge job itself
  // (src/lib/inngest/cleanupAnonymousProjects.ts). Keyed on the project owner,
  // never a synthetic id: a founder whose flag is off never saw the two-stage
  // UI, so their projects must not be destroyed on a promise they were never
  // shown. The read fails closed — an unknown verdict (PostHog outage) means
  // "do not destroy", and the job then does only its anonymous-expiry work.
  // Kill-switch note (LEV-2016): turning this OFF stops purges, but the
  // soft-deleted rows keep aging. Turning it back ON purges everything already
  // past the window at the next run, in one batch. That is intended — the
  // 30-day window is a promise about elapsed time, not about job uptime. It
  // does NOT undo a purge already done, and it does not unstick a row whose
  // `purgeStartedAt` is set: that project is no longer restorable either way.
  PROJECT_HARD_DELETE: "project-hard-delete",
  // Gates the redesigned logged-out homepage. ON → gradient hero with three
  // capability cards that each send a starter message to the onboarding chat,
  // a Training section (whose topic chips also send starters) in place of
  // Mission, and a scrolling "Companies built & funded on the Lev process"
  // logo strip; How it Works' "Build with Lev" buttons send a general opener
  // instead of scrolling to the top. OFF (production default) → today's
  // astronaut hero + Mission section. Client-gated only (pure UI, no server
  // surface); evaluated with the visitor's anonymous PostHog distinct id.
  // Independent of anonymous-projects. `?flags=home-v2` previews it anywhere.
  LANDING_HOME_V2: "landing-home-v2",
  // LEV-2006: cap of 20 Apollo-sourced prospects per founder, counted as the
  // Apollo prospects they currently hold across all their projects (no weekly
  // or monthly reset; deleting one frees a spot). Raises are hand-edited
  // VARIANTS in the PostHog dashboard: a variant per raise level whose payload
  // is the bare cap (e.g. `cap-50` → 50, integer 1..10000), with a release
  // condition pinning the founder's distinct id to it. Never put a map of user
  // ids in a payload: posthog-js ships payloads to the browser. Read by
  // getProspectCapOverride in src/lib/featureFlags/server.ts. Independent of
  // PROSPECTING_AGENT, which gates the agentic Customers empty-state CTA.
  // ON (any variant) → the cap applies on every Apollo path: the "Add from
  // Apollo" menu entry and `pipelines.startApolloImport` become reachable
  // (one Apollo import in flight per founder), the agentic flow's weekly-100
  // cap is replaced by this cap, the Discovery deliverable job (previously
  // uncapped) is capped too, and a "request more" popup appears once the
  // quota is used up. OFF (default) → pre-feature behavior exactly: no "Add
  // from Apollo" entry and `startApolloImport` refuses, the agentic flow keeps
  // its weekly 100 cap, the Discovery job stays uncapped, no popup.
  // User-scoped (keyed on the founder whose quota is being spent, i.e. the
  // project owner). Fail-closed.
  PROSPECTING_CAP: "prospecting-cap",
  // LEV-2392: sibling of PROSPECTING_CAP. ON → the Customers add menu shows
  // "Add from Apollo" even when `prospecting-cap` is off. Choosing it still
  // does not search and does not open the request-more alert until
  // `prospecting-cap` is on; over the cap, that choice opens the alert.
  // OFF → today's menu: the item exists only while `prospecting-cap` is on.
  // Keyed on the project owner, same as the cap. Fail-closed. Does not make
  // `startApolloImport` reachable; that stays behind `prospecting-cap`.
  PROSPECTING_APOLLO_MENU: "prospecting-apollo-menu",
  // LEV-1093: gates hover/focus hint tooltips on the Customers pipeline
  // toolbar controls (search, add menu, kanban/table toggle, stage settings,
  // properties) and their `pipeline_toolbar_hint_viewed` event. ON → each
  // control shows its `pipelineToolbarHints` copy after a 500ms hover or on
  // keyboard focus, and the viewed event fires after a 600ms hover dwell or
  // a keyboard focus, once per control per session. OFF → no hint bubbles and
  // no viewed event; the kanban/table toggle buttons keep their native `title`
  // attrs. Everything else on the board ships regardless of this flag (see
  // PIPELINE_TOUR). Independent of `pipeline-tour`: either, both, or neither
  // may be on; the two share only their copy source (`ADD_MENU_ITEM_COPY`),
  // never their flag.
  PIPELINE_TOOLBAR_HINTS: "pipeline-toolbar-hints",
  // Gates the guided "tour" of the Customers pipeline board (the other
  // teaching surface, alongside the toolbar hints) and its "Take the tour"
  // button in the card header's top-right controls, plus the tour's lifecycle
  // events. ON → the tour auto-starts at most twice per browser per pipeline
  // kind (manual starts never count) and the "Take the tour" button starts it
  // any time; while the tour runs, the toolbar hints
  // are hidden so the two don't compete for the same controls. OFF
  // (production default) → no tour, no "Take the tour" button, no tour
  // events. Ships regardless of this flag and `pipeline-toolbar-hints`
  // (LEV-2106): the Customers add-menu relabel ("Find more customers"), its
  // item renames and sub-lines, the removal of the standalone "Find new
  // Prospects" header button, the read-only add-menu fix, and the empty
  // second header row in the shared lightbox header; board analytics
  // (`pipeline_board_viewed`, `pipeline_board_action`) also fire regardless
  // so the control group is measurable. Shares its copy with the hints (both
  // build the add menu's copy from `ADD_MENU_ITEM_COPY`) but never the flag:
  // either may be on independently.
  PIPELINE_TOUR: "pipeline-tour",
  // Gates the guided tour of the Overall Evaluation panel and its lifecycle
  // events. ON → the tour auto-starts once per browser when the panel opens
  // with results loaded (viewport 768px or wider), and a "Take the tour"
  // button in the header's right-hand group, just before the close X, replays
  // it. OFF → the panel is unchanged: no tour, no button, no tour events. Independent of
  // `pipeline-tour`; the two task stops are skipped when the eval page has no
  // task rows yet.
  EVALUATION_TOUR: "evaluation-tour",
  // Gates the Overall Evaluation's per-grade improvement narrative. ON → the
  // evaluation runs in two stages (one grading call, then one call per area
  // graded below A using EVAL_NARRATIVE_GUIDANCE), stored as
  // StructuredEvaluationWithNarrativeSchema: every B–F grade carries a short
  // plain-text "why this grade / what you could do" narrative shown on the
  // criterion card, and its improvements are one founder task plus one Lev
  // task; Re-evaluate is routed through the shared evaluateProjectNow path.
  // OFF → exactly today's prompt, schema, card and task shape. Evaluated
  // against the PROJECT OWNER on every server surface so a collaborator never
  // sees a mixed result. The narrative text renders above the criterion's
  // generated task list; independent of `eval-stable-results`. Fail-closed.
  EVAL_TASKS_NARRATIVE: "eval-tasks-narrative",
  // Gates consistent evaluation results. ON → evaluateProjectNow hashes every
  // byte the model would see (prompt, model, reasoning effort, schema id) and
  // reuses the stored result when nothing changed (no model call), keeps the
  // other rubric's last result in inputSnapshot.priorRubric so a rubric switch
  // back returns the same grades on any device, and the browser localStorage
  // evaluation cache step is skipped. OFF → every evaluation calls the model
  // and the stored row has no inputHash/priorRubric, exactly as today.
  // Evaluated against the PROJECT OWNER. Independent of
  // `eval-tasks-narrative`: the narrative prompt and schema id are inside the
  // hash, so toggling the narrative flag forces one fresh run. Fail-closed.
  EVAL_STABLE_RESULTS: "eval-stable-results",
  // Team-only testing hook for the phased evaluation (First read). ON → the
  // Overall Evaluation header shows a "First read (team)" link and
  // `evaluation.previewFirstRead` generates an unsaved First read from the
  // current sections (nothing stored, no grades touched); its one button
  // sends the Customer Discovery "work on" message into Lev chat. OFF → the page is
  // unchanged and the endpoint returns NOT_FOUND. Evaluated against the ACTING
  // user, not the project owner. MUST stay targeted to the Internal Users
  // cohort while the preview link exists: widening it shows founders a
  // team-only testing tool. Not `?flags=`-overridable. Independent of
  // `eval-tasks-narrative` and `eval-stable-results`. Fail-closed.
  EVAL_PHASED_AREAS: "eval-phased-areas",
  // Brutal read on the Overall Evaluation, for every founder. ON → a "Brutal
  // read" link sits on the Summary line of the full evaluation and of the
  // First read, and `evaluation.previewBrutalRead` generates an unsaved
  // skeptical-investor read (a short narrative and the likeliest reasons the
  // company fails; no grades, nothing stored, one model call per open). OFF →
  // no link, and the endpoint returns NOT_FOUND. Independent of
  // `eval-first-read-journey` below — the First read placement no longer
  // needs `eval-phased-areas` once the journey ships. Rolled out to everyone
  // and kept as the kill switch for cost or quality. Evaluated against the
  // ACTING user. Not `?flags=`-overridable. Fail-closed.
  EVAL_BRUTAL_READ: "eval-brutal-read",
  // LEV-1681: the founder-facing phased Overall Evaluation (Locked -> First
  // read -> Full), derived from which of Competitive Landscape, Market Size
  // and Customer Problem have content (see src/lib/evaluation/journeyState.ts).
  // ON → the lightbox shows a Locked card, an ungraded First read, or the
  // graded Full page depending on that derived state; `evaluateNow`/
  // `reEvaluate`/content-change auto-grading stay closed until `full`; the
  // Full page's overall rating becomes a letter grade "based on N of M areas"
  // (src/lib/evaluation/overallGrade.ts). OFF → exactly today's single-phase
  // graded evaluation, byte-for-byte. Evaluated against the PROJECT OWNER on
  // every surface (client, tRPC, chat tool), never the acting collaborator.
  // Independent of `eval-phased-areas`, which stays the team-only "First read
  // (team)" link and is NOT repurposed by this flag, and of
  // `eval-brutal-read` and `eval-tasks-narrative`/`eval-stable-results`.
  // Fail-closed.
  EVAL_FIRST_READ_JOURNEY: "eval-first-read-journey",
  // Gates the checkpoint-path evaluation tasks (task-eval-sync branch). ON →
  // each Overall Evaluation area graded below A produces one outcome Quest
  // with 3–8 ordered checkpoint tasks (discovery/decision/creation, one
  // owner, a done-when, and what to record for the next task); downstream
  // checkpoints stay locked until the earlier ones are done, and checkpoint
  // details (the "Done when"/"Record" lines, title rewrites) are written only
  // once a checkpoint is added. Eval becomes the single task source: the v3
  // task engine (freeflow generation, the buffer top-up, Engine 2's cursor)
  // generates nothing for the owner, so Brand and Team — areas the engine
  // covers but eval's rubric doesn't grade — surface no tasks at all. The
  // sidebar shows unlocked, not-yet-added eval checkpoints as suggestions in
  // place of engine-generated ones. A re-evaluation runs when an eval path's
  // last checkpoint task completes, so the area's grade can move before the
  // next path is planned. OFF → exactly today's evaluation AND today's
  // engine: one founder task plus one Lev task per area, today's prompt,
  // schema, input hash, card and task shape, and the v3 engine generating as
  // it always has. Gates everything built on the task-eval-sync branch.
  // Evaluated against the PROJECT OWNER on every server surface (chat tool,
  // tRPC, Inngest job) so a collaborator never sees a mixed result. REQUIRES
  // `eval-tasks-narrative` ON — it has no narrative to reshape otherwise, so
  // turn both on together. `tasks-v4` is independent except for one
  // override: while this flag is ON, v4 seeding (generateV4Tasks, the
  // after-canvas dispatch, the freeflow v4 branch) stands down so eval stays
  // the only task source. In the sidebar, rows the founder already has
  // (engine or v4 suggestions revealed before the flip) stay visible; only
  // never-revealed engine rows are hidden. Toggling it changes the
  // evaluation input hash, so `eval-stable-results` forces exactly one fresh evaluation per project
  // rather than reusing a stored result built under the other shape.
  // Fail-closed. NOT `?flags=`-overridable: Inngest jobs (the details job,
  // the reconcile job) cannot read the browser's override cookie, so an
  // override would make the stream route and the job disagree about which
  // shape to build.
  TASK_EVAL_SYNC: "task-eval-sync",
  // Umbrella gate for the onboarding + tasks revamp built on
  // `feature/onboarding-and-tasks`. ON → every new behavior this branch
  // introduces across onboarding and tasks. OFF (production default) →
  // pre-branch legacy behavior everywhere, byte-for-byte. Every new
  // user-facing behavior on the branch MUST be gated by this flag (or a
  // named sibling) before it merges — the OFF state reproduces the whole
  // pre-feature experience, not a subset. Gated on every surface: client
  // hook for UI, fail-closed server helper for tRPC/API, and at the
  // dispatch point for any Inngest job. Keyed on the acting user (owner for
  // project-scoped generation). Default OFF in production.
  ONBOARDING_TASKS: "onboarding-tasks",
  // Umbrella gate for the tasks-v4 model (stage-as-rung + evidence completion):
  // the v4 task generator, evidence-completion closure,
  // the idempotent stage advance, and proto auto-regen on completion. ON → the
  // whole v4 experience for the acting owner. OFF (production default) →
  // tasks-v3 as-is, byte-for-byte, on every surface. Every v4 behavior MUST be
  // gated by this flag (or a named sibling) on every surface it touches: client
  // hook for UI, fail-closed server helper for tRPC/API + injected config, and
  // at the dispatch point for any Inngest job. Keyed on the acting user (project
  // owner for project-scoped generation). Distinct from ONBOARDING_TASKS, which
  // gates the separate onboarding-chat/deepen-fork work.
  TASKS_V4: "tasks-v4",
  // Customer Discovery rebuild, row-order module
  // (docs/plans/2026-10-07-customer-discovery-row-order-spec.md). ON: projects created while on get
  // the rebuilt Customer Discovery row (stamped per project; existing
  // projects unaffected). OFF: new projects get today's four-tile row. Read
  // ONCE at project creation (`readCustomerDiscoveryV2Verdict`) and stamped to
  // `Project.customerDiscoveryV2`; every later read of the row shape uses the
  // stamp, never a live flag re-evaluation — a project never flips rows when
  // the flag changes after it exists, matching the proto-blocks stamp pattern
  // (PROTO_BLOCKS above). Keyed on the creating user's distinct id (the
  // anonymous visitor's id for an anonymous project).
  CUSTOMER_DISCOVERY_V2: "customer-discovery-v2",
  // Customer Discovery v2 sibling: the ICP lightbox as a step-by-step survey
  // (one question per step, then confirm, then a result page with Lev's
  // feedback). ON: the `icp-builder` lightbox shows the survey, and a save
  // from it keeps only the one buyer, as the bet. OFF: today's dials-and-
  // clusters lightbox, unchanged. Pure UI over the same `icp.*` mutations, so
  // client-gated only. Only reachable on projects stamped
  // `customerDiscoveryV2` (no ICP tile exists otherwise). Keyed on the viewer.
  CUSTOMER_DISCOVERY_V2_ICP_SURVEY: "customer-discovery-v2-icp-survey",
  // Customer Discovery v2 sibling (of customer-discovery-v2 and
  // customer-discovery-v2-icp-survey): lets the owner of a project created
  // before v2 (`customerDiscoveryV2` false) opt it in. ON: the collapsed
  // Customer Discovery card shows "Find Customer Targets" to the owner; a
  // click stamps the project v2, adds the empty ICP tile and starts Lev's
  // draft of the ICP. OFF: today's card, and `icp.migrateLegacyProject`
  // refuses. Never stamps a NEW project: creation reads only
  // customer-discovery-v2. Keyed on the project owner (server) and the viewer
  // (client), which are the same person because only the owner sees the button.
  CUSTOMER_DISCOVERY_V2_MIGRATE: "customer-discovery-v2-migrate",
  // Sibling of CUSTOMER_DISCOVERY_V2_ICP_SURVEY, read only while that flag
  // shows the survey. ON: the survey asks for the industry (its own step,
  // after the buyer) and for words that name the buyer (on the buyer step);
  // a save stores them, and the count and the Customer Targets search send
  // them to Apollo as `q_organization_keyword_tags`. OFF: today's four
  // questions, saves store neither, and Apollo gets today's filters. Keyed on
  // the project OWNER: the client hook (only the owner edits), the
  // `icp.save` / `icp.updateDials` / `icp.count` server gate, and a boolean
  // captured at the `icp.save` / `icp.acceptSuggestion` dispatch for the
  // Customer Targets job, which never reads the flag. Opening the survey
  // fills an empty industry or keyword list from the approved Customer
  // Discovery, unless those lists were already cleared (`icp.fillIndustryKeywords`,
  // never inside `icp.get`). Fail-closed.
  CUSTOMER_DISCOVERY_V2_INDUSTRY_KEYWORDS: "customer-discovery-v2-industry-keywords",
  // LEV-2757: the founder can name their ICP in their own words. ON: LevAI
  // gets the choose_icp tool and the prompt rules that go with it (withheld
  // via excludeToolNames otherwise, tools/optionalTools.ts), the Customer
  // Discovery chips add "None of these. I'll describe my own", the choice
  // chips also show on an APPROVED Customer Discovery while no ICP is chosen,
  // and an ICP tile save records a committed ICP when none exists. OFF:
  // choose_icp is withheld from the tool schema (excludeToolNames), and the
  // baked system prompt's conditional ICP wording (config.ts
  // PROMPT_ICP_CHOICE / PROMPT_FOUNDER_MATERIAL) falls back to its "tool is
  // not available" branch — the founder commits an ICP with the canvas
  // chips, and Lev applies the framing through propose_edit. Independent of
  // customer-discovery-v2. Fail-closed.
  LEVAI_TYPED_ICP: "levai-typed-icp",
  // LEV-2243: the canvas accelerator promo. With accelerators-row OFF: ON
  // shows the legacy YC banner and YC Application block for founders who
  // have not started an application (still subject to dismissal, the deadline
  // and the YC-funnel rule), and ProgramApplicationBanner; OFF hides them. A
  // founder with a started YC application always sees their block, whatever
  // this flag says (useYcCanvas). With accelerators-row ON: ON shows the
  // Programs nudge (ProgramsBanner, Lev's one-time chat line and its chip;
  // useProgramsNudge), subject to its own dismissal; OFF shows none of it.
  // Loading or unknown counts as OFF. Client-gated. Keyed on the viewer.
  ACCELERATOR_BANNER: "accelerator-banner",
  // TEST-ONLY (slated for deletion after testing): gate the onboarding Stage Map
  // observability board. ON → a left-rail icon opens a read-only kanban of the 3
  // stages, each task showing a not-done / says-done-low-confidence / done-T1
  // status derived from onboarding evidence + live completion, plus a
  // `current-<confidence>` label on the founder's stage. The board opens only
  // when the founder clicks the icon (no auto-open). OFF (default) → no icon, no
  // board — byte-for-byte the current experience. Client-gated (the icon guards
  // the only caller of the read-only query). User-scoped.
  ONBOARDING_STAGE: "onboarding-stage",
  // In-canvas task cards rendered at each section's end, with their
  // open/delete/defer/promote actions and server-resolved assignee avatars.
  // ON → the cards render; OFF → no in-canvas tasks at all. Client-gated in
  // OwnerCanvas/MemberCanvas: the per-step `getTasksForStep` query is skipped
  // and no task props are forwarded, so the read is never issued when OFF.
  // Default OFF in production.
  TASK_IN_CANVAS: "task-in-canvas",
  // LEV-2323: gates the Lev MCP server. ON: the founder's MCP requests are
  // served. OFF: every MCP request for that founder is 401'd, including live
  // connections (kill switch, checked per request). Keyed on the founder's
  // Clerk user id from their OAuth token; fails closed when PostHog is down.
  LEV_MCP: "lev-mcp-server",
  // LEV-2358: founder-idea fit story. ON: Team block fit story panel,
  // teamFounderFit tRPC, fit story fed to accelerator generators and
  // evaluators. OFF: none of these, identical to today. Keyed on the acting
  // user; fails closed when PostHog is down.
  TEAM_FOUNDER_FIT: "team-founder-fit",
  // LEV-2496: Lev MCP write tools (update_document, create_task,
  // update_task_status). Sibling of `lev-mcp-server`, which must also be ON
  // for any MCP request to be served. ON: the write tools are listed and
  // run. OFF: write tools are absent from the tool list and refused if
  // called; reads keep working (kill switch for writes alone). Keyed on the
  // founder's Clerk user id; fails closed when PostHog is down.
  LEV_MCP_WRITES: "lev-mcp-writes",
  // LEV-2555: Venture Graph review tools on the Lev MCP server (vg_queue_*,
  // vg_firm*, vg_filing, vg_latest_run). Only matters when `lev-mcp-server`
  // is also ON (that flag decides whether any MCP request is served);
  // independent of `lev-mcp-writes`. ON: a caller with a verified Lev team
  // email sees and runs the vg_ tools, writes included. OFF: the vg_ tools
  // are absent from the tool list and refused if called, and no Clerk team
  // lookup runs. Keyed on the caller's Clerk user id; fails closed when
  // PostHog is down. MUST target the Lev team only, never a percentage
  // rollout: every MCP request the flag is ON for makes a Clerk user lookup,
  // and Clerk's API is rate-limited, so founder traffic under the flag would
  // spend that budget on lookups that can only say "not team".
  LEV_MCP_VENTURE_GRAPH: "lev-mcp-venture-graph",
  // Gate task generation on canvas completion. ON: no tasks generate (and the
  // task panel shows "Set up your Lev canvas first") until the whole canvas is
  // GENERATED (every Create-All step has a non-PROTO primary + its deliverables)
  // AND REVIEWED (every Create-All primary is APPROVED — the founder clicked the
  // orange "Looks good" signoff chip); tasks then generate on the approval or
  // deliverable that completes the canvas. Holds only the INITIAL run: once the
  // project has Quests the gate never re-closes, even when a regenerated primary
  // drops back to DRAFT. OFF (default) → today's behavior byte-for-byte: tasks
  // unlock the moment the Customer Discovery block is generated. Gated on every
  // surface it touches — the fail-closed server helper for the tRPC preflights
  // (actions.getCurrentWeek/generate/replenish) + v4 seed, the approve-path
  // dispatch, and the empty-state copy. Keyed on the acting user (project owner
  // for project-scoped generation); fails closed when PostHog is down.
  TASKS_AFTER_CANVAS: "tasks-after-canvas",
  // LEV-1947: gates the project-sector classifier job, Project.sectors
  // storage, and the founder-correction surface. ON = a new project gets
  // sectors classified and can see/correct them. OFF = no new classification
  // is dispatched and no sector UI renders (pre-feature behavior); runs
  // already queued may still finish one sweep batch (up to 50 projects).
  // Server-authoritative at the classify-dispatch point and the tRPC
  // read/write procedures. Keyed on the project owner, or on
  // anonProjectFlagDistinctId(anonDistinctId) for a pre-claim anonymous
  // project, whose sentinel owner id is shared by every visitor.
  PROJECT_SECTOR_CLASSIFICATION: "project-sector-classification",
  // LEV-2357: launches the Techstars format in the Accelerator Apps Labs
  // feature. ON → the Techstars card appears next to YC in the format picker,
  // and accelerators.applications.generate accepts "techstars"
  // (question-by-question application and per-question scoring from
  // src/constants/accelerators/questions/techstars.ts). OFF (default) →
  // today's behavior: Techstars is not in the picker and the router refuses it
  // with FORBIDDEN. Independent of labs-all (the key has no `labs-` prefix, so
  // the labs-all payload never turns it on). Interview questions follow the
  // format's spec plus the owner's accelerators-row flag alone
  // (evaluationIncludesInterviewPrep); row OFF means no interview prep,
  // never this flag. Keyed on the acting user.
  ACCELERATOR_APPS_TECHSTARS: "accelerator-apps-techstars",
  // LEV-2359: program landers (Techstars first). ON: /techstars program lander
  // + program-aware onboarding chat + Techstars canvas banner + program_*
  // events. OFF: today's /techstars exactly. Evaluated with the visitor's
  // anonymous distinct id on public surfaces (`anon:<tokenHash>` in the chat
  // route) and the signed-in user's id after signup. Fail-closed. Independent
  // of anonymous-projects: both may be ON, and the lander works either way.
  ACCELERATORS_STRAIGHT_IN: "accelerators-straight-in",
  // Accelerators canvas row. ON: the owner's canvas gains a row below every
  // other block, a Programs block (the programs the founder is applying to or
  // tracking, tagged Applying or Tracking) and one "<Program> Application"
  // block per program the founder applies to, each opening the Accelerator
  // Apps panel in a lightbox; `accelerators.getRow` serves it. Its Programs
  // page is one list: every catalog program with its location, format, Lev's
  // read and a Program details link, and Start a draft for the programs Lev
  // can draft (Techstars and YC); the rest can be tracked. OFF (default): today's canvas
  // exactly, and `accelerators.getRow` is FORBIDDEN. Keyed on the acting user,
  // who is always the owner (the row is owner-only). Its cohort MUST be a
  // subset of accelerator-apps-techstars: the row hides Techstars when that
  // flag is off, so a viewer outside it sees a row without the program the
  // row was built for. Fail-closed.
  ACCELERATORS_ROW: "accelerators-row",
  // accelerators-program-suggester: Lev's program suggestions on the
  // Programs page (see ACCELERATORS_ROW above). ON: the page adds "Lev
  // recommends" (Lev's top three) above All programs and a "Lev suggests"
  // panel (what to highlight, the gap to close) on every row Lev judged, and
  // accelerators.suggestPrograms runs the matcher, one gpt-5.4-mini call per
  // automatic first run or Refresh, storing the result on the project.
  // OFF (default): the page lists every program under All programs, with Lev's
  // read and no recommendations, and accelerators.suggestPrograms is
  // FORBIDDEN; no model call is made.
  // Requires accelerators-row: without the row there is no Programs page.
  // Keyed on the acting user, who is always the owner. Fail-closed.
  ACCELERATORS_PROGRAM_SUGGESTER: "accelerators-program-suggester",
  // accelerators-suggester-deck (LEV-2442): what the program suggester reads.
  // ON: accelerators.suggestPrograms also reads the stated metrics and
  // fact-bearing slides of the deck from the onboarding session the owner
  // claimed for this project, and adds them to the prompt as a data block.
  // OFF (default): canvas blocks only, today's prompt byte for byte, and no
  // session read. Evaluated only when accelerators-program-suggester is ON
  // (with the suggester off the procedure is FORBIDDEN first). Keyed on the
  // acting user, who is always the owner. Fail-closed.
  ACCELERATORS_SUGGESTER_DECK: "accelerators-suggester-deck",
  // accelerators-weekly-email (LEV-2623): a Programs section in the
  // founder's Monday weekly report email. ON (and accelerators-row also ON
  // for the project owner): the email lists up to three programs, first the
  // ones the founder is applying to, then the ones Lev recommends, read from
  // the suggestions the Programs page already stored (the email job never
  // runs the suggester or any model call), each with its deadline when one is
  // known and still ahead, plus a link that opens Programs on the canvas. A
  // project applying to nothing with no stored strong-fit or worth-a-shot
  // suggestion gets no section. OFF (either flag, or unknown): today's email
  // exactly. Evaluated per project owner in the email job, outside any step,
  // before the send slot is claimed.
  // Follows accelerators-program-suggester for recommendations: with it OFF
  // the Programs page hides "Lev recommends", so the email lists only the
  // programs the founder is applying to, and the email and its destination
  // page never disagree. Fail-closed.
  ACCELERATORS_WEEKLY_EMAIL: "accelerators-weekly-email",
  // fundraising-think-bigger: the Think Bigger vision slider in Fundraising's
  // Raise Summary stage, and the compact version of the same card on the
  // Investor Deck stage (LEV-2442). ON: Raise Summary shows the five-level
  // slider that rewrites the raise summary paragraph as a bolder investor
  // story, next to "What the greats said"; the Investor Deck stage shows a
  // condensed card with the same slider, one quote, and a link back to the
  // full card. Either stage's card also requires a Raise summary to scale;
  // thinkBigger.investorStory runs one model call per new level, from
  // whichever stage asked. OFF (default): Fundraising renders exactly as it
  // does today on both stages, and thinkBigger.investorStory is FORBIDDEN.
  // Independent of every accelerator flag. Keyed on the acting user.
  // Fail-closed.
  FUNDRAISING_THINK_BIGGER: "fundraising-think-bigger",
  // LEV-2395: when the Big Idea finishes its post-write work. ON: once the
  // Big Idea version and its Summary block commit, `generateIdeaStatement`
  // responds, and the rest of the claim-fenced tail (content changed, tasks,
  // activity, analytics, compass refresh, terminal success, claim close) runs
  // under `after()` with the claim heartbeat kept alive; spotlight intel then
  // announces itself with a `job:spotlight.updated` event. OFF (and unknown):
  // the whole tail runs inside the request, exactly as before. The public YC
  // path never reads it. Keyed on the acting user, fail-closed, independent of
  // every other flag. Not `?flags=`-overridable.
  IDEA_DEFERRED_FINISH: "idea-deferred-finish",
  // phone-handoff (LEV-2457): phone = start, computer = work. ON: on a phone
  // (max-width 639px AND a coarse pointer), a signed-in founder's canvas
  // shows "Lev's tools work best on a computer" in place of every block,
  // feature lightbox and Labs page, with a summary of what Lev built and an
  // "Email me a link" button; once after the first canvas build (or at a
  // premier program's first stop) Lev emails that summary automatically,
  // once per project. Lev chat, onboarding, sign-up, the first build, the
  // Programs picker and weekly reports / investor updates stay usable.
  // phoneHandoff.getSummary and phoneHandoff.sendLink serve it. OFF
  // (default): every surface renders exactly as today on every device, and
  // both procedures are FORBIDDEN. Desktop and tablets never see it.
  // Independent of every other flag. Keyed on the acting user. Fail-closed.
  PHONE_HANDOFF: "phone-handoff",
  // Dismissible "New: apply to Techstars, YC and more" banner on the
  // signed-in home page, for returning founders only (>=1 project). ON (and
  // `accelerators-row` also ON): the banner shows until dismissed; its
  // button opens the Accelerators row's Programs block, which only exists
  // with the row, so the row flag is a hard requirement, not a cosmetic one.
  // OFF (either flag): no banner — pre-feature behavior. Dismissal is stored
  // in Clerk `unsafeMetadata.dismissedAnnouncements`, not this flag, so
  // turning it back on does not re-show the banner to someone who already
  // dismissed it. Keyed on the signed-in user. Loading/unknown reads as off
  // (useFeatureFlag's default coercion), so the banner fails closed.
  ACCELERATORS_ANNOUNCEMENT: "accelerators-announcement",
  // task-kanban: the task kanban board's local-day model (By Date/By
  // Dimension views, tz-aware day boundaries, "No date" retirement, the
  // round-checkbox complete/undo, the year-out defer horizon). ON also:
  // a dated task is committed work — it keeps its date through Today
  // instead of clearing on arrival, and a day left unstarted reads as a
  // derived "Overdue" (By Date's fixed leftmost column, a status label in
  // By Dimension and the sidebar's Current) rather than silently resetting;
  // a half-hourly scheduled job (ADR 080) flips a dated pending task to
  // in_progress on its owner-local day and starts Lev on a Lev-startable
  // one at 06:00 owner-local, never catching up a missed day; a non-owner
  // collaborator may change only tasks explicitly assigned to them (plus
  // Lev/agent tasks, and, with `canAddTasks`, unassigned Suggested tasks —
  // everyone else's and unassigned tasks otherwise stay owner-only); Lev
  // chat and MCP task creation accept an optional date and each chat turn
  // gets a "Today is …" context line. OFF (default) → today's UTC-boundary
  // kanban and today's permission model, byte-for-byte; the scheduled job
  // dispatches nothing. Turning it OFF is lossy for far dates: the next
  // OFF-path reconcile's rule 2 clears `scheduledFor` on every active row
  // dated beyond the current week (a year-out defer loses its date for
  // good). Keyed on the PROJECT OWNER's distinct id on EVERY
  // surface, never the acting collaborator's. NOT `?flags=`-overridable: a
  // per-browser split would let a flag-OFF session's reconcile rule 2
  // (beyond-week date → no_date) silently wipe a future-week date a
  // flag-ON session on the SAME project just set. Fail-closed. See ADR 079
  // and ADR 080.
  TASK_KANBAN: "task-kanban",
  // Lev work review: Edit on Lev's proposal cards (Type A) and Reject + Edit
  // beside "Approve Task Completion" on approval requests (Type B). ON:
  // founders/approvers can send feedback instead of only Accept/Reject (and
  // Approve-only for Type B); a proposal Edit sends the feedback back to Lev
  // for revision, an approval Reject soft-deletes the block Lev created in
  // that run when untouched (no APPROVED status, no non-"ai" versions, no
  // comments). ON also hides the lightbox comment bar on a Lev-completable
  // task (the card's Edit/Reject carry feedback), except while Lev's
  // post-Reject revision prompt waits on a lightbox comment (task-lev-to-chat
  // OFF; with it ON that loop runs in the side chat). OFF (default): only
  // Accept/Reject on proposals and only Approve on completion requests, bar
  // rules unchanged — today's behavior byte-for-byte.
  // Rejected/accepted/revised history rendering in the activity log is
  // UNGATED (ships regardless of this flag). Independent of every other
  // flag. Keyed on the acting user. Fail-closed.
  TASK_PROPOSAL_EDIT: "task-proposal-edit",
  // LEV-2666: Lev announces Programs in chat. ON (and `accelerators-row` also
  // ON for the project owner, since Programs only exist with the row): on one
  // chat turn per project, Lev ends its reply by telling the founder about
  // Programs (Techstars and YC drafted from their canvas) and asking whether
  // they plan to apply. Only for the owner, only while the canvas shows
  // Programs with no program picked, never on a task turn, never for a
  // program-lander or YC public-app founder, never after the Programs banner
  // was dismissed. The instruction is injected per turn, not baked into the
  // agent, so no agent version bump. The turn it rides on stamps
  // `uiState.programsLevaiAnnounced`, which also stops the canned Programs
  // chat line (accelerator-banner) from describing Programs again. OFF
  // (either flag): no instruction, no uiState write, today's chat byte for
  // byte. Keyed on the project owner. Fail-closed.
  ACCELERATORS_LEVAI_ANNOUNCE: "accelerators-levai-announce",
  // LEV-2351 / LEV-2634: ON = a signed-in viewer sees the Facts inspector
  // beside the expanded onboarding chat, showing facts from their OWN
  // onboarding session. OFF or unknown = no panel (fails closed). Client-only:
  // nothing on the server reads this flag. GET /api/onboarding/facts/debug
  // requires a signed-in session and serves only the caller's own cookie
  // session regardless of this flag, so the flag decides visibility only.
  // `?flags=facts` forces it on for a signed-in viewer. Independent of every
  // other flag.
  FACTS_DEBUG_INSPECTOR: "facts-debug-inspector",
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];
